import { z } from '@hono/zod-openapi'

export const SsoProviderSchema = z.enum([
  'google', 'microsoft', 'oidc-generic', 'saml',
]).openapi({ example: 'google', description: 'SSO identity provider' })

export const OidcLoginRequestSchema = z.object({
  provider: SsoProviderSchema,
  code: z.string().openapi({ description: 'Authorization code from IdP' }),
  redirectUri: z.string().url().openapi({ example: 'https://app.cetused.com/auth/callback' }),
  nonce: z.string().nullable().openapi({ description: 'Replay-attack nonce' }),
  state: z.string().nullable().openapi({ description: 'CSRF state parameter' }),
  codeVerifier: z.string().nullable().openapi({ description: 'PKCE code verifier' }),
}).openapi('OidcLoginRequest')

export const OidcLoginResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number().int(),
  idToken: z.string().openapi({ description: 'JWT ID token from provider' }),
  user: z.object({
    sub: z.string(),
    email: z.string().email(),
    name: z.string(),
    givenName: z.string().nullable(),
    familyName: z.string().nullable(),
    picture: z.string().url().nullable(),
    locale: z.string().nullable(),
  }),
}).openapi('OidcLoginResponse')

export const SamlAssertionSchema = z.object({
  samlResponse: z.string().openapi({ description: 'Base64-encoded SAML Response XML' }),
  relayState: z.string().nullable(),
  provider: z.literal('saml'),
}).openapi('SamlAssertion', { description: 'SAML 2.0 assertion from IdP' })

export const GoogleIdentityTokenClaimsSchema = z.object({
  iss: z.string().url().openapi({ example: 'https://accounts.google.com' }),
  sub: z.string(),
  aud: z.string(),
  exp: z.number().int(),
  iat: z.number().int(),
  email: z.string().email(),
  email_verified: z.boolean(),
  name: z.string(),
  picture: z.string().url().nullable(),
  given_name: z.string().nullable(),
  family_name: z.string().nullable(),
  locale: z.string().nullable(),
  hd: z.string().nullable().openapi({ description: 'G Suite hosted domain' }),
}).openapi('GoogleIdentityTokenClaims')

export const MicrosoftIdentityTokenClaimsSchema = z.object({
  iss: z.string().url().openapi({ example: 'https://login.microsoftonline.com/{tenant}/v2.0' }),
  sub: z.string(),
  aud: z.string(),
  exp: z.number().int(),
  iat: z.number().int(),
  email: z.string().email().nullable(),
  preferred_username: z.string().nullable(),
  name: z.string(),
  oid: z.string().openapi({ description: 'Object ID in Entra ID' }),
  tid: z.string().openapi({ description: 'Tenant ID' }),
  roles: z.array(z.string()).nullable(),
  ver: z.string().openapi({ example: '2.0' }),
}).openapi('MicrosoftIdentityTokenClaims')

export const SsoConfigSchema = z.object({
  provider: SsoProviderSchema,
  clientId: z.string(),
  clientSecret: z.string().openapi({ description: 'Write-only' }),
  issuerUrl: z.string().url().nullable(),
  scopes: z.array(z.string()).default(['openid', 'profile', 'email']),
  redirectUri: z.string().url(),
  metadataUrl: z.string().url().nullable().openapi({ description: 'OIDC discovery URL or SAML metadata URL' }),
  active: z.boolean(),
}).openapi('SsoConfig', { description: 'SSO provider configuration for a district' })

export type SsoProvider = z.infer<typeof SsoProviderSchema>
export type OidcLoginRequest = z.infer<typeof OidcLoginRequestSchema>
export type OidcLoginResponse = z.infer<typeof OidcLoginResponseSchema>
export type SamlAssertion = z.infer<typeof SamlAssertionSchema>
export type GoogleIdentityTokenClaims = z.infer<typeof GoogleIdentityTokenClaimsSchema>
export type MicrosoftIdentityTokenClaims = z.infer<typeof MicrosoftIdentityTokenClaimsSchema>
export type SsoConfig = z.infer<typeof SsoConfigSchema>
